/**
 * @file Client-side audit-log emitter. Fire-and-forget call:
 *
 *   import { logAuditEvent } from './audit-log.js';
 *   import { AUDIT_ACTION } from './lib/audit-actions.js';
 *
 *   logAuditEvent({
 *     action: AUDIT_ACTION.DEVICE_HANDOVER,
 *     subject: { type: 'device', id: device.id, label: device.name },
 *     details: { from: 'in_use', to: 'returned' },
 *   });
 *
 * Writes to `audit_log/{auto-id}` with a server timestamp, in the STRV
 * standard entry shape (playbook §8): { ts, action, actor, subject?,
 * people?, details? } — auditForward copies each entry to the IT Hub. The Firestore
 * rule pins `actor.uid` to `request.auth.uid` so callers can't spoof
 * another user's identity. Failures are logged to console and swallowed —
 * audit emits must never block the user-facing action that produced them.
 */

import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

import { db, auth }          from './firebase.js';
import { AUDIT_ACTION_VALUES } from './lib/audit-actions.js';
import { testCollectionName } from './test-mode.js';
import { sessionOrigin }      from './lib/session-origin.js';

const COLL = 'audit_log';

/**
 * Emit an audit event. Fire-and-forget — returns a Promise<boolean> for
 * tests but production callers should NOT await; the user shouldn't wait
 * on the audit write.
 *
 * @param {object}  args
 * @param {string}  args.action   - One of AUDIT_ACTION.*.
 * @param {object} [args.subject] - `{ type, id, label? }` — what the action was about.
 * @param {string[]} [args.people] - work emails of everyone the action is about
 *                                  (feeds the IT Hub's person timeline).
 * @param {object} [args.details] - Action-specific payload (kept small).
 * @param {object} [args.actor]   - Override actor. Useful for sign-in: at
 *                                  the moment of emit the app may not yet
 *                                  have wired auth state into module
 *                                  globals, but `firebase.auth().currentUser`
 *                                  is set.
 */
export function logAuditEvent({ action, subject, people, details, actor } = {}) {
  if (!action || !AUDIT_ACTION_VALUES.has(action)) {
    console.warn('[audit] logAuditEvent: missing or unknown action', action);
    return Promise.resolve(false);
  }

  const resolvedActor = actor || actorFromAuth();
  if (!resolvedActor) {
    // No signed-in user — nothing useful to record. (sign-in path always
    // passes an explicit actor since this can race the auth state update.)
    return Promise.resolve(false);
  }

  const doc = {
    ts:    serverTimestamp(),
    action,
    actor: cleanActor(resolvedActor),
  };
  const cleanedSubject = cleanSubject(subject);
  if (cleanedSubject) doc.subject = cleanedSubject;
  const cleanedPeople = cleanPeople(people);
  if (cleanedPeople.length) doc.people = cleanedPeople;
  if (details)       doc.details = details;

  return addDoc(collection(db, testCollectionName(COLL)), doc)
    .then(() => true)
    .catch((err) => {
      console.warn('[audit] write failed:', action, err?.code || err?.message || err);
      return false;
    });
}

function actorFromAuth() {
  const user = auth?.currentUser;
  if (!user) return null;
  return {
    type:  'user',
    uid:   user.uid,
    email: user.email || null,
    name:  user.displayName || null,
    ...(sessionOrigin() === 'hub' ? { via: 'hub' } : {}),
  };
}

function cleanActor(actor) {
  const out = { type: actor.type || 'user' };
  if (actor.uid)   out.uid   = String(actor.uid);
  if (actor.email) out.email = String(actor.email);
  if (actor.name)  out.name  = String(actor.name);
  if (actor.via)   out.via   = String(actor.via);
  return out;
}

function cleanPeople(people) {
  const out = new Set();
  for (const v of people || []) {
    const e = typeof v === 'string' ? v.trim().toLowerCase() : '';
    if (e.includes('@')) out.add(e);
  }
  return [...out].slice(0, 20);
}

function cleanSubject(subject) {
  if (!subject?.type || !subject?.id) return undefined;
  const out = { type: String(subject.type), id: String(subject.id) };
  if (subject.label) out.label = String(subject.label).slice(0, 200);
  return out;
}
